import React from 'react'

import cx from 'classnames'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { TASK_STATUS } from '@constants'

import { TaskListCheckedIcon } from '../svgIcons'
import { Typography } from '../Typography'
import { Accordion } from './Accordion'

const CHECKED_CLASSES = {
  checked: 'checked',
  disabled: 'disabled',
  unchecked: 'unchecked',
}

const StyledTaskListCheckedIcon = styled(TaskListCheckedIcon)`
  flex-shrink: 0;

  --unchecked-color: ${({ theme }) => theme.colors.primary[50]};
  --checked-color: ${({ theme }) => theme.colors.success[40]};

  & rect {
    transition: all 0.3s ease-in-out;
  }

  /* increased specificity required due to Accordion's transition overwrite */
  && path {
    stroke-linejoin: round;
    stroke-linecap: round;
    stroke: var(--checked-color);
    stroke-dasharray: 31;
    stroke-dashoffset: 31;
    transition: all 0.3s ease-out;
  }

  &.${CHECKED_CLASSES.checked} {
    rect {
      stroke: var(--checked-color);
    }

    path {
      stroke-dashoffset: 0;
    }
  }

  &.${CHECKED_CLASSES.unchecked} {
    rect {
      stroke: var(--unchecked-color);
    }

    path {
      stroke-opacity: 0;
      stroke-dashoffset: -31;
    }
  }

  &.${CHECKED_CLASSES.disabled} {
    color: inherit;

    rect {
      stroke: currentColor;
    }
  }
`

const HeaderWrapperTypography = styled(Typography)<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  /* so the header can be narrower than its text, rather than pushing past the chevron beside it */
  min-width: 0;
  /* a floor rather than a height: a title too long for one line wraps instead of leaving the box */
  min-height: 59px;
  padding: 8px 16px;
  gap: 20px;
  color: ${({ theme, $disabled }) => ($disabled ? 'inherit' : theme.colors.gray[175])};
`

const TitleSpan = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
  text-align: left;
`

const ContentWrapperDiv = styled.div`
  padding: 6px 16px 16px;
  background-color: ${({ theme }) => theme.colors.gray[0]};
`

interface BaseTaskListAccordionProps {
  children: NonNullable<React.ReactNode>
  index: number
  name: string
  open: boolean
  openHandler?: (name: string, isExpanding: boolean) => void
  status: 'current' | 'skipped' | 'completed' | 'disabled'
  title: StringOrMessage
}

/**
 * Accordion component for a task list design with checkbox icon display representing the state of the task.
 *
 * @param {BaseTaskListAccordionProps} { index, title, open, children, status }
 */
export function TaskListAccordion({
  children,
  index,
  name,
  open,
  openHandler: openHandlerProp,
  status,
  title,
}: BaseTaskListAccordionProps) {
  const isCompleted = status === TASK_STATUS.completed || status === TASK_STATUS.skipped
  const isDisabled = status === TASK_STATUS.disabled

  function openHandler(isExpanding: boolean) {
    openHandlerProp?.(name, isExpanding)
  }

  return (
    <Accordion
      disabled={isDisabled}
      headerContent={
        <HeaderWrapperTypography size="700-md" tag="span" $disabled={isDisabled}>
          <StyledTaskListCheckedIcon
            className={cx({
              [CHECKED_CLASSES.checked]: isCompleted,
              [CHECKED_CLASSES.unchecked]: !isCompleted,
              [CHECKED_CLASSES.disabled]: isDisabled,
            })}
          />
          <TitleSpan>
            <FormattedMessage
              id="accordion.taskList.title"
              defaultMessage="{index}. {title}"
              values={{
                index,
                title,
              }}
            />
          </TitleSpan>
        </HeaderWrapperTypography>
      }
      open={open}
      {...(openHandlerProp && { openHandler })}
      variant="card"
    >
      {props => {
        return (
          <ContentWrapperDiv>
            {React.Children.map(children, child => {
              // Checking isValidElement is the safe way and avoids a typescript error too.
              if (React.isValidElement(child)) {
                return React.cloneElement(child, props)
              }
              return child
            })}
          </ContentWrapperDiv>
        )
      }}
    </Accordion>
  )
}
